import * as React from 'react';
import './Billing.scss';
import { TK } from '../../store/translations/translationKeys';
import { Translate, useTranslations } from '../../store/translations/hooks';
import { ButtonGroup, Button, Alert, Modal, ModalHeader, ModalBody, ListGroup, ListGroupItem } from 'reactstrap';
import StyledTable from '../../components/StyledTable';
import moment from 'moment';
import { PaymentData, PaymentOrigin, PaymentStatus } from '../../models';
import { useDispatch, useSelector } from 'react-redux';
import { useEntitiesResult } from '../../store/entities/hooks';
import { PaymentEntity } from '../../store/entities/state';
import { isLoadingEntities } from '../../store/entities/selectors';
import { loadEntities, openEntity, openAppointment } from '../../store/entities/actions';
import { downloadInvoicesSaftFileAsync, getAdminPaymentsAsync, getClientOrderByIdAsync, getPackAsync } from '../../ajax/requests';
import { getErrorMessage } from '../../ajax/errors';
import Typography from '../../components/Typography';
import { hide } from '../../shared/style';
import { formatEuros } from '../../shared/string';
import { BillingResponsiveBar } from './BillingResponsiveBar';
import { CSVLink } from 'react-csv';
import { useCookies } from 'react-cookie';
import { DateRangeSelector, DateRangeSelectorState } from '../../components/DateRangeSelector';
import { canSeeInvoices } from '../../store/session/selectors';
import { alertError, alertGenericError } from '../../store/alerts/actions';
import { cookiesOptions } from '../../shared/utils';
import { AutocompleteTextField, SwitchField } from '../../components/Forms/fields';
import { useBillingGroupBy } from '../../hooks/useBillingGroupBy';
import { TotalsGrid, StatCard, CardLabel, CardValue, CardSubValue } from './BillingStatCards';
import { PaymentLabel } from 'src/components/Forms/fields/PaymentsList';


interface Coiso {
    values: Record<string, number>
    counts: Record<string, number>
}

interface BillItem {
    label: string,
    data: PaymentEntity[],
    values: Record<string, number>,
    counts: Record<string, number>,
}

const originTk = (origin: PaymentOrigin, plural: boolean = false): TK => {
    switch (origin) {
        case PaymentOrigin.Appointment: return plural ? TK.appointments : TK.appointment
        case PaymentOrigin.Pack: return plural ? TK.packs : TK.pack
        case PaymentOrigin.Order: return TK.orders
        default: return TK.unknown
    }
}

const getTotal = (payments: PaymentData[]): {value: number, count: number} => payments.reduce((acc, p) => ({
    value: acc.value + p.value,
    count: acc.count + 1,
}), {value: 0, count: 0})

const getTotalsByOrigin = (t: Translate<TK>, payments: PaymentData[]) => payments.reduce<Coiso>((acc, p) => {
    const key = t(originTk(p.origin, true))
    return {
        values: {
            ...acc.values,
            [key]: (acc.values[key]??0) + p.value,
        },
        counts: {
            ...acc.counts,
            [key]: (acc.counts[key]??0) + 1,
        },
    }
}, {values: {}, counts: {}})

const getTotalsByType = (t: Translate<TK>, payments: PaymentData[]) => payments.reduce<Coiso>((acc, p) => ({
    values: {
        ...acc.values,
        [p.type]: (acc.values[p.type]??0) + p.value,
    },
    counts: {
        ...acc.counts,
        [p.type]: (acc.counts[p.type]??0) + 1,
    },
}), {values: {}, counts: {}})
  

function* getDataByDay(
    t: Translate<TK>, 
    payments: PaymentData[],
    splitBy: 'byOrigin'|'byType',
    groupBy: 'byDay'|'byWeek'|'byMonth',
    window: DateRangeSelectorState,
): Generator<BillItem> {
    const dateFormat = groupBy === 'byMonth' ? 'YYYY-MM' : 'YYYY-MM-DD'

    const perDay = payments.reduce<Record<string, PaymentData[]>>((acc, data) => {
        const day = groupBy === 'byWeek' ? moment.utc(data.date).startOf('isoWeek').format(dateFormat)
            : groupBy === 'byMonth' ? moment.utc(data.date).startOf('month').format(dateFormat)
            : data.date.substring(0, 10)
        return {
            ...acc,
            [day]: acc[day] ? [...acc[day], data] : [data],
        };
    }, {})

    const from = groupBy === 'byWeek' ? moment.utc(window.from).startOf('isoWeek')
            : groupBy === 'byMonth' ? moment.utc(window.from).startOf('month')
            : moment.utc(window.from)

    const to = moment.utc(window.to).add(1, 'day')
    let current = from

    while (current < to) {
        const day = current.format(dateFormat)
        const data = perDay[day] ?? []
        const total = getTotal(data)
        const splitTotals = splitBy === 'byOrigin' ? getTotalsByOrigin(t, data) : getTotalsByType(t, data)
        yield {
            label: day,
            data,
            values: {
                [t(TK.total)]: total.value,
                ...splitTotals.values,
            },
            counts: {
                [t(TK.total)]: total.count,
                ...splitTotals.counts,
            },
        }
        current = current.add(1, groupBy === 'byWeek' ? 'weeks' : groupBy === 'byMonth' ? 'months' : 'days')
    }
}

const Payments: React.FC = () => {
    const t = useTranslations()
    const dispatch = useDispatch()
    const _payments = useEntitiesResult<PaymentEntity>('payments')
    const isLoading = useSelector(isLoadingEntities('payments'))
    
    const hasInvoicesFeature = useSelector(canSeeInvoices)
    
    const [cookies, setCookie] = useCookies()

    const [showWarningMsg, setShowWarningMsg] = React.useState(!cookies['PAYMENTS_NOTE'])
    const closeWarningMsg = React.useCallback(() => {
        setShowWarningMsg(false)
        setCookie('PAYMENTS_NOTE', 'SHOWN', cookiesOptions)
    }, [setShowWarningMsg, setCookie])

    const [splitBy, setSplitBy] = React.useState<'byOrigin'|'byType'>('byType')
    const [groupBy, setGroupBy] = useBillingGroupBy()
    const [dontGroup, setDontGroup] = React.useState<boolean>(!!cookies['BILLING_DONT_GROUP'])
    const [dates, setDates] = React.useState<DateRangeSelectorState | undefined>()
    const [downloadingSaft, setDownloadingSaft] = React.useState(false)
    const [paymentsToShow, showPayments] = React.useState<PaymentEntity[]>([])
    const [filters, setFilters] = React.useState<string[]>([])
    const [invertFilter, setInvertFilter] = React.useState(false)
    const [withoutVAT, setWithoutVAT] = React.useState(false)
    const [withoutComission, setWithoutComission] = React.useState(false)

    const payments = _payments.filter(p => {
        if (p.status !== PaymentStatus.Manual && p.status !== PaymentStatus.Success) return false
        if (!filters.length) return true
        const matches = filters.includes(p.clientName) || filters.includes(t(originTk(p.origin))) || filters.includes(p.type) || filters.includes(p.cashRegisterName ?? '') || filters.includes(p.username ?? '')
        return invertFilter ? !matches : matches
    })

    React.useEffect(() => {
        if (dates) {
            const diff = moment.utc(dates.to).diff(moment.utc(dates.from), 'months')
            setGroupBy(diff <= 1 ? 'byDay' : 'byMonth')
            dispatch(loadEntities('payments', () => getAdminPaymentsAsync(dates.from, dates.to), 'NewResult', payments.map(x => x.id)))
        }
    }, [dates])
            
    const hasVAT = React.useMemo(() => payments.some(p => p.vat), [payments])

    const totalVAT = React.useMemo(() =>
        payments.reduce((acc, p) => acc + (p.vat ? p.value - p.value / (1 + p.vat) : 0), 0)
    , [payments])

    const paymentsWithComission = React.useMemo(() => payments.filter(p => (p.comissionWithoutVat ?? 0) > 0), [payments])
    const hasComission = React.useMemo(() => paymentsWithComission.length > 0, [payments])

    const totalComission = React.useMemo(() =>
        paymentsWithComission.reduce((acc, p) => acc + (p.comissionWithoutVat ? p.comissionWithoutVat * 1.23 : 0), 0)
    , [paymentsWithComission])

    const processedPayments = React.useMemo(() => {
        let result = payments
        if (withoutVAT) result = result.map(p => p.vat ? { ...p, value: p.value / (1 + p.vat) } : p)
        if (withoutComission) result = result.map(p => p.comissionWithoutVat ? { ...p, value: p.value - p.comissionWithoutVat * 1.23 } : p)
        return result
    }, [payments, withoutVAT, withoutComission])

    const dataByDay: BillItem[] = React.useMemo(
        () => dates && [...getDataByDay(t, processedPayments, splitBy, groupBy, dates)] || [],
        [splitBy, processedPayments, dates]);

    const total = React.useMemo(() => getTotal(processedPayments), [processedPayments])

    const totals = React.useMemo(() => ([
        getTotalsByOrigin(t, processedPayments),
        getTotalsByType(t, processedPayments),
    ]), [processedPayments])

    const splitTotals = splitBy === 'byOrigin' ? totals[0] : totals[1]

    const splitVATTotals = React.useMemo(() =>
        payments.reduce<Record<string, number>>((acc, p) => {
            const vatAmount = p.vat ? p.value - p.value / (1 + p.vat) : 0
            if (!vatAmount) return acc
            const key = splitBy === 'byOrigin' ? t(originTk(p.origin, true)) : p.type
            return { ...acc, [key]: (acc[key] ?? 0) + vatAmount }
        }, {})
    , [payments, splitBy])
        
    const onClick = React.useCallback((payment: PaymentEntity) => () => {
      if(payment.origin === PaymentOrigin.Appointment) {
        dispatch(openAppointment(payment.originId))
      } else if(payment.origin === PaymentOrigin.Pack) {
        dispatch(openEntity('clientPacks', payment.originId, () => getPackAsync(payment.originId)))
      } else if(payment.origin === PaymentOrigin.Order) {
        dispatch(openEntity('orders', payment.originId, () => getClientOrderByIdAsync(payment.originId) as any))
      }
    }, [dispatch])
     
    const columns = React.useMemo(() => dataByDay.length ? [...new Set(dataByDay.flatMap(d => Object.keys(d.values)))] : [], [dataByDay]);

    const allData = processedPayments.map((p, i) => ({
        id: p.id,
        date: p.date.substring(0, 16).replace('T', ' '),
        client: p.clientName,
        origin: t(originTk(p.origin)),
        type: p.type,
        cashRegisterName: p.cashRegisterName,
        username: p.username,
        value: formatEuros(p.value),
        vatAmount: payments[i].vat ? payments[i].value - payments[i].value / (1 + payments[i].vat) : undefined,
        comissionWithVAT: payments[i].comissionWithoutVat ? payments[i].comissionWithoutVat * 1.23 : undefined,
    }))

    const handleExportSaft = () => {
        if(downloadingSaft || !dates) return
        setDownloadingSaft(true)
        const year = dates.from.substring(0, 4)
        const month = dates.from.substring(5, 7)
        downloadInvoicesSaftFileAsync(year, month)
            .then(response => {
                if ('url' in response) window.open(response.url)
                else dispatch(alertError(response.message))
            })
            .catch((error) => {
                const message = getErrorMessage(error)
                if (message) dispatch(alertError(message))
                else dispatch(alertGenericError())
            })
            .finally(() => setDownloadingSaft(false))
    }

    return (
        <div className='billingContainer'>
            {isLoading && <Typography style={{position: 'fixed', bottom: 0, left: 0, backgroundColor: 'white'}}>{t(TK.loading)}...</Typography>}
            
            <Alert color="secondary" isOpen={showWarningMsg} toggle={closeWarningMsg}>
                <div dangerouslySetInnerHTML={{__html: t(TK.paymentsBillingDescription)}}/>
            </Alert>
            
            <DateRangeSelector onRangeChanged={setDates}/>

            <div className="billTypePanel">
                <ButtonGroup >
                    <Button outline active={splitBy === 'byType'} onClick={() => setSplitBy('byType')}>{t(TK.byPaymentType)}</Button>
                    <Button outline active={splitBy === 'byOrigin'} onClick={() => setSplitBy('byOrigin')}>{t(TK.by)} {t(TK.origin)}</Button>
                </ButtonGroup>
                <ButtonGroup >
                    <Button outline color="info" active={groupBy === 'byDay'} onClick={() => setGroupBy('byDay')}>{t(TK.byDay)}</Button>
                    <Button outline color="info" active={groupBy === 'byWeek'} onClick={() => setGroupBy('byWeek')}>{t(TK.byWeek)}</Button>
                    <Button outline color="info" active={groupBy === 'byMonth'} onClick={() => setGroupBy('byMonth')}>{t(TK.byMonth)}</Button>
                </ButtonGroup>
                <AutocompleteTextField
                    options={[...new Set(allData.flatMap(d => [d.client, d.origin, d.type, d.cashRegisterName, d.username].filter((v): v is string => !!v)))]}
                    values={filters}
                    onValuesChanged={setFilters}
                    multiSelect
                    placeholder={`${t(TK.filter)}...`}
                />
            </div>

            { isLoading ? <Typography>{t(TK.loading)}...</Typography>
                : <>
                    <div style={{display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 'var(--spacer-M)', padding: 'var(--spacer-XS) 0', borderBottom: 'var(--border-thin) solid var(--appLigthGrey)', marginBottom: 'var(--spacer-S)'}}>
                        {filters.length > 0 && (
                            <SwitchField label={t(TK.invertFilterResult)} value={invertFilter} onChange={setInvertFilter} />
                        )}
                        {hasVAT && (
                            <SwitchField label={t(TK.withoutVAT)} value={withoutVAT} onChange={setWithoutVAT} />
                        )}
                        {hasComission && (
                            <SwitchField label={t(TK.withoutComission)} value={withoutComission} onChange={setWithoutComission} />
                        )}
                    </div>

                    <TotalsGrid>
                        <StatCard accent="var(--success-color)">
                            <CardLabel>{t(TK.total)}</CardLabel>
                            <CardValue textColor="var(--success-color)">{formatEuros(total.value)}</CardValue>
                            <CardSubValue>{total.count} {t(TK.payments).toLowerCase()}</CardSubValue>
                            {hasVAT && !withoutVAT && totalVAT > 0 && (
                                <CardSubValue>{t(TK.vat)}: {formatEuros(totalVAT)}</CardSubValue>
                            )}
                        </StatCard>
                        {hasComission && totalComission > 0 && (
                            <StatCard accent="var(--warning-color)" style={{cursor: 'pointer'}} onClick={() => showPayments(processedPayments.filter(p => (p.comissionWithoutVat ?? 0) > 0))}>
                                <CardLabel>{t(TK.comission)}</CardLabel>
                                <CardValue textColor="var(--warning-color)">{formatEuros(totalComission)}</CardValue>
                            <CardSubValue>{paymentsWithComission.length} {t(TK.payments).toLowerCase()}</CardSubValue>
                            </StatCard>
                        )}
                        {Object.entries(splitTotals.values).map(([key, value]) => (
                            <StatCard
                                key={key}
                                accent="var(--appLigthGrey)"
                                style={{cursor: 'pointer'}}
                                onClick={() => showPayments(processedPayments.filter(p =>
                                    splitBy === 'byOrigin' ? t(originTk(p.origin, true)) === key : p.type === key
                                ))}
                            >
                                <CardLabel><PaymentLabel paymentType={key}/></CardLabel>
                                <CardValue textColor="var(--text-body)">{formatEuros(value)}</CardValue>
                                <CardSubValue>{splitTotals.counts[key]} {t(TK.payments).toLowerCase()}</CardSubValue>
                                {hasVAT && !withoutVAT && splitVATTotals[key] > 0 && (
                                    <CardSubValue>{t(TK.vat)}: {formatEuros(splitVATTotals[key])}</CardSubValue>
                                )}
                            </StatCard>
                        ))}
                    </TotalsGrid>

                    <BillingResponsiveBar
                        data={dataByDay.map(({label, values}) => ({label, ...values}))}
                        keys={
                            splitBy === 'byOrigin' ? Object.keys(totals[0].values) : 
                            splitBy === 'byType' ? Object.keys(totals[1].values) : 
                            [t(TK.total)]
                        }
                        onClick={(id, item) => splitBy === 'byOrigin' ? showPayments(dataByDay.find(d => d.label === item.label)?.data.filter(d => t(originTk(d.origin, true)) === id) ?? [])
                            : splitBy === 'byType' ? showPayments(dataByDay.find(d => d.label === item.label)?.data.filter(d => d.type === id) ?? [])
                            : undefined}
                    />
                    
                    <div style={{display: 'flex', gap: 'var(--spacer-XS)', justifyContent: 'end', position: 'relative', top: '-30px'}}>
                        <SwitchField 
                            style={{display: 'inline'}}
                            label={t(TK.dontGroup)}
                            value={dontGroup}
                            id='dont-group'
                            onChange={e => {
                                const val = !!e
                                setDontGroup(val)
                                setCookie('BILLING_DONT_GROUP', val ? '1' : '', cookiesOptions)
                            }}
                        />
                    </div>

                    <div className="tableContainer" style={hide(!dontGroup)}>
                        <StyledTable scrollable>
                            <thead>
                                <tr>
                                    <th>{t(TK.date)}</th>
                                    <th>{t(TK.client)}</th>
                                    <th>{t(TK.origin)}</th>
                                    <th>{t(TK.type)}</th>
                                    <th>{t(TK.payment)}</th>
                                    {hasVAT && !withoutVAT && <th>{t(TK.vat)}</th>}
                                    {hasComission && <th>{t(TK.comission)}</th>}
                                </tr>
                            </thead>
                            <tbody>
                            {allData.map((p, i) => (
                                <tr key={p.id}>
                                    <td>{p.date}</td>
                                    <td>{p.client}</td>
                                    <td><Button size="sm" color="link" onClick={onClick(processedPayments[i])}>{p.origin}</Button></td>
                                    <td><PaymentLabel paymentType={p.type} /></td>
                                    <td>{p.value}</td>
                                    {hasVAT && !withoutVAT && <td style={{color: 'var(--text-muted)'}}>{p.vatAmount != null ? formatEuros(p.vatAmount) : '-'}</td>}
                                    {hasComission && <td style={{color: 'var(--text-muted)'}}>{p.comissionWithVAT != null ? formatEuros(p.comissionWithVAT) : '-'}</td>}
                                </tr>
                            ))}
                            </tbody>
                        </StyledTable>
                    </div>

                    <div className="billingTable" style={hide(dontGroup)}>
                        <StyledTable>
                            <thead>
                                <tr>
                                    <th/>
                                    { columns.map(column => <th key={column}>{column}</th>)}
                                </tr>
                            </thead>
                            <tbody>
                                { dataByDay.map((item, i) =>
                                    <tr key={`${item.label}-${i}`} onClick={() => showPayments(item.data)} style={{cursor: 'pointer'}}>
                                        <td>{item.label}</td>
                                        { columns.map(column =>
                                            <td key={column}>{`${formatEuros(item.values[column] ?? 0)} (${item.counts[column] ?? 0})`}</td>
                                        )}
                                    </tr>
                                )}
                            </tbody>
                        </StyledTable>
                    </div>

                    <CSVLink 
                        data={allData} 
                        separator=';' 
                        target='_blank'
                        filename='payments-export.csv'
                    >
                        {t(TK.exportData)}
                    </CSVLink>
                    
                    {hasInvoicesFeature && <a href="javascript:void(0);" onClick={handleExportSaft}>{t(TK.exportSaft)}</a>}
                </>
            }

            <Modal size='lg' isOpen={!!paymentsToShow.length} toggle={() => showPayments([])}>
                <ModalHeader toggle={() =>  showPayments([])}>{t(TK.expenses)}</ModalHeader>
                <ModalBody>
                    <ListGroup>
                        {paymentsToShow.map(e => (
                            <ListGroupItem
                                key={e.id}
                                onClick={onClick(e)}
                                style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer'}}
                            >
                                <div>
                                    <div>
                                        <b>{e.clientName}</b> ({t(originTk(e.origin))})
                                    </div>
                                    <div>
                                        <PaymentLabel paymentType={e.type}/> - {e.date.substring(0, 10)}
                                    </div>
                                </div>
                                <div style={{textAlign: 'right'}}>
                                    <b>{formatEuros(e.value)}</b>
                                    {!!e.vat && (
                                        <div style={{fontSize: '0.78rem', color: 'var(--text-muted)'}}>
                                            {t(TK.vat)}: {formatEuros(e.value - e.value / (1 + e.vat))}
                                        </div>
                                    )}
                                </div>
                            </ListGroupItem>
                        ))}
                    </ListGroup>
                </ModalBody>
            </Modal>
        </div>
    );
}

export default Payments;
